import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { mdiFileTreeOutline, mdiLinkVariant, mdiLinkVariantOff, mdiLoading, mdiPlus } from '@mdi/js'
import { $api } from '../../api/queryClient'
import { apiClient } from '../../api/client'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { formatIssueReference } from '../../lib/issue-reference'
import { errorCode, invalidateIssueQueries, proofLabel } from './issueFamily'
import { suggestionConfig, type SuggestionIssue } from './suggestionKit'
import { useTicketSubtypes } from '../../hooks/useTicketSubtypes'

/** A child row as the list endpoint returns it. */
type ChildRow = {
    uid: string
    number?: number
    title: string
    status: string
    subtype?: string | null
    due_date?: string | null
    wait_for?: Record<string, unknown> | null
    assigned_to?: { name?: string | null; email?: string | null } | null
    properties?: Record<string, unknown> | null
}

type ParentIssue = {
    uid: string
    status: string
    target_entity_type?: string | null
    target_entity_uid?: string | null
    children_summary?: { total: number; open: number; in_progress: number; closed: number; draft: number } | null
}

const ALL_STATUSES = 'draft,open,in_progress,closed'

/**
 * The "Sous-tickets" card of a ticket (spec 2026-10-27-19 § 6): the progress
 * line, one row per child in step order, and the buttons to add a sub-ticket,
 * attach an existing one, or detach one. Starting a scenario is spec
 * 2026-10-28-15's.
 */
export function IssueChildrenCard({ orgCode, parent, onCloseParent }: {
    orgCode: string
    parent: ParentIssue
    /** Opens the parent's close flow (the "all closed" shortcut). */
    onCloseParent: () => void
}) {
    const { t, i18n } = useTranslation('issues')
    const queryClient = useQueryClient()
    const [addOpen, setAddOpen] = useState(false)
    const [attachOpen, setAttachOpen] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const { byCode } = useTicketSubtypes(orgCode)

    const childrenQuery = $api.useQuery('get', '/api/v1/organizations/{orgCode}/issues', {
        params: {
            path: { orgCode },
            query: { parent_uid: parent.uid, status: ALL_STATUSES, assignee_uid: 'all', type: '*', limit: 100 } as never,
        },
    })
    const rows = useMemo(() => (childrenQuery.data?.data ?? []) as unknown as ChildRow[], [childrenQuery.data])
    const titleByCode = useMemo(() => {
        const out: Record<string, string> = {}
        for (const r of rows) {
            const code = r.properties?.workflow_step
            if (typeof code === 'string') out[code] = r.title
        }
        return out
    }, [rows])

    const total = rows.length
    const done = rows.filter((r) => r.status === 'closed').length
    const allClosed = total > 0 && done === total && parent.status !== 'closed'

    const detach = async (uid: string) => {
        setError(null)
        const { error: err } = await apiClient.PATCH('/api/v1/organizations/{orgCode}/issues/{issueUid}', {
            params: { path: { orgCode, issueUid: uid } },
            body: { parent_issue_uid: null } as never,
        })
        if (err) setError(t('children.error', { reason: errorCode(err) ?? '' }))
        invalidateIssueQueries(queryClient)
    }

    const now = Date.now()

    return (
        <Card data-testid="issue-children-card">
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <AppIcon path={mdiFileTreeOutline} size="sm" />
                    {t('children.card_title')}
                    {total > 0 && (
                        <span className="text-sm font-normal text-muted-foreground tabular-nums" data-testid="issue-children-progress">
                            {t('children.progress', { done, total })}
                        </span>
                    )}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                {childrenQuery.isLoading ? (
                    <AppIcon path={mdiLoading} size="sm" className="motion-safe:animate-spin" />
                ) : total === 0 ? (
                    <p className="text-sm text-muted-foreground" data-testid="issue-children-empty">{t('children.empty')}</p>
                ) : (
                    <ul className="divide-y rounded-md border" data-testid="issue-children-list">
                        {rows.map((r) => {
                            const draft = r.status === 'draft'
                            const overdue = (r.status === 'open' || r.status === 'in_progress') && r.due_date && new Date(r.due_date).getTime() < now
                            const after = ((r.properties?.step as { after?: string[] } | undefined)?.after ?? [])
                                .map((c) => titleByCode[c] ?? c).join(', ')
                            const waiting = r.status !== 'closed' ? proofLabel(t, r.wait_for) : ''
                            const look = suggestionConfig(r as unknown as SuggestionIssue, byCode)
                            return (
                                <li
                                    key={r.uid}
                                    className={`flex items-center gap-2 px-3 py-2 text-sm ${draft ? 'opacity-60' : ''}`}
                                    data-testid={`issue-child-row-${r.uid}`}
                                >
                                    <AppIcon path={look.icon} size="sm" className={`flex-shrink-0 ${look.accent}`} />
                                    <div className="min-w-0 flex-1">
                                        <Link
                                            to="/$org/issues/$uid"
                                            params={{ org: orgCode, uid: r.uid }}
                                            className="text-foreground hover:underline"
                                            data-testid={`issue-child-link-${r.uid}`}
                                        >
                                            <span className="text-muted-foreground tabular-nums mr-1.5">
                                                {formatIssueReference(r.number, i18n.language)}
                                            </span>
                                            {r.title}
                                        </Link>
                                        <div className="flex flex-wrap items-center gap-1.5 mt-0.5 text-[11px] text-muted-foreground">
                                            <Badge variant="outline" className="text-[11px]" data-testid={`issue-child-status-${r.uid}`}>
                                                {t(`status.${r.status}`, { defaultValue: r.status })}
                                            </Badge>
                                            <span>{r.assigned_to?.name || r.assigned_to?.email || t('children.unassigned')}</span>
                                            {r.due_date && <span>{new Date(r.due_date).toLocaleDateString(i18n.language)}</span>}
                                            {overdue && (
                                                <Badge variant="destructive" className="text-[11px]" data-testid={`issue-child-overdue-${r.uid}`}>
                                                    {t('children.overdue')}
                                                </Badge>
                                            )}
                                            {draft && after && <span>{t('children.after_step', { step: after })}</span>}
                                            {waiting && (
                                                <Badge variant="secondary" className="text-[11px]" data-testid={`issue-child-waiting-${r.uid}`}>
                                                    {t('children.waiting_for', { proof: waiting })}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7"
                                        aria-label={t('children.detach')}
                                        title={t('children.detach')}
                                        onClick={() => detach(r.uid)}
                                        data-testid={`issue-child-detach-${r.uid}`}
                                    >
                                        <AppIcon path={mdiLinkVariantOff} size="xs" />
                                    </Button>
                                </li>
                            )
                        })}
                    </ul>
                )}
                {allClosed && (
                    <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm" data-testid="issue-children-all-closed">
                        <span>{t('children.all_closed')}</span>
                        <Button size="sm" onClick={onCloseParent} data-testid="issue-children-close-parent">
                            {t('children.close_parent')}
                        </Button>
                    </div>
                )}
                {error && <p className="text-sm text-destructive" data-testid="issue-children-error">{error}</p>}
                <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => setAddOpen(true)} data-testid="issue-children-add">
                        <AppIcon path={mdiPlus} size="xs" className="mr-1" />
                        {t('children.add')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setAttachOpen(true)} data-testid="issue-children-attach">
                        <AppIcon path={mdiLinkVariant} size="xs" className="mr-1" />
                        {t('children.attach')}
                    </Button>
                </div>
            </CardContent>
            <AddChildDialog orgCode={orgCode} parent={parent} open={addOpen} onOpenChange={setAddOpen} />
            <AttachChildDialog orgCode={orgCode} parentUid={parent.uid} open={attachOpen} onOpenChange={setAttachOpen} />
        </Card>
    )
}

/** "Ajouter un sous-ticket": a title, the parent's target prefilled. */
function AddChildDialog({ orgCode, parent, open, onOpenChange }: {
    orgCode: string
    parent: ParentIssue
    open: boolean
    onOpenChange: (open: boolean) => void
}) {
    const { t } = useTranslation('issues')
    const queryClient = useQueryClient()
    const [title, setTitle] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const submit = async () => {
        setBusy(true)
        setError(null)
        const target = parent.target_entity_type === 'asset'
            ? { asset_uid: parent.target_entity_uid ?? undefined }
            : parent.target_entity_uid
                ? { target_entity_uid: parent.target_entity_uid, target_type: parent.target_entity_type ?? undefined }
                : {}
        const { error: err } = await apiClient.POST('/api/v1/organizations/{orgCode}/issues/{issueUid}/children', {
            params: { path: { orgCode, issueUid: parent.uid } },
            body: { title: title.trim(), ...target } as never,
        })
        setBusy(false)
        if (err) {
            setError(t('children.error', { reason: errorCode(err) ?? '' }))
            return
        }
        setTitle('')
        invalidateIssueQueries(queryClient)
        onOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md" data-testid="issue-child-create-dialog">
                <DialogHeader>
                    <DialogTitle>{t('children.create_title')}</DialogTitle>
                    <DialogDescription>{t('children.create_description')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground" htmlFor="issue-child-title">{t('children.title_label')}</label>
                    <Input
                        id="issue-child-title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && title.trim()) submit() }}
                        data-testid="issue-child-title-input"
                    />
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="issue-child-create-cancel">{t('children.cancel')}</Button>
                    <Button onClick={submit} disabled={!title.trim() || busy} data-testid="issue-child-create-submit">
                        {t('children.create_submit')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

/** "Rattacher un ticket": search by number or title, pick one. */
function AttachChildDialog({ orgCode, parentUid, open, onOpenChange }: {
    orgCode: string
    parentUid: string
    open: boolean
    onOpenChange: (open: boolean) => void
}) {
    const { t, i18n } = useTranslation('issues')
    const queryClient = useQueryClient()
    const [q, setQ] = useState('')
    const [error, setError] = useState<string | null>(null)
    const search = $api.useQuery('get', '/api/v1/organizations/{orgCode}/issues', {
        params: { path: { orgCode }, query: { q, assignee_uid: 'all', status: 'draft,open,in_progress', limit: 10 } as never },
    }, { enabled: open && q.trim().length > 0 })
    const found = ((search.data?.data ?? []) as unknown as ChildRow[]).filter((r) => r.uid !== parentUid)

    const pick = async (uid: string) => {
        setError(null)
        const { error: err } = await apiClient.PATCH('/api/v1/organizations/{orgCode}/issues/{issueUid}', {
            params: { path: { orgCode, issueUid: uid } },
            body: { parent_issue_uid: parentUid } as never,
        })
        if (err) {
            setError(t('children.error', { reason: errorCode(err) ?? '' }))
            return
        }
        invalidateIssueQueries(queryClient)
        setQ('')
        onOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md" data-testid="issue-child-attach-dialog">
                <DialogHeader>
                    <DialogTitle>{t('children.attach_title')}</DialogTitle>
                    <DialogDescription>{t('children.attach_description')}</DialogDescription>
                </DialogHeader>
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('children.attach_search')} data-testid="issue-child-attach-search" />
                <ul className="max-h-64 overflow-y-auto divide-y rounded-md border">
                    {q.trim() && found.length === 0 && !search.isLoading && (
                        <li className="px-3 py-2 text-sm text-muted-foreground">{t('children.attach_none')}</li>
                    )}
                    {found.map((r) => (
                        <li key={r.uid}>
                            <button
                                type="button"
                                className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50"
                                onClick={() => pick(r.uid)}
                                data-testid={`issue-child-attach-option-${r.uid}`}
                            >
                                <span className="text-muted-foreground tabular-nums mr-1.5">{formatIssueReference(r.number, i18n.language)}</span>
                                {r.title}
                            </button>
                        </li>
                    ))}
                </ul>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="issue-child-attach-cancel">{t('children.cancel')}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
